import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import {
  TuiButtonModule,
  TuiDialogService,
  TuiGroupModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import {
  TUI_PROMPT,
  TuiInputModule,
  TuiInputNumberModule,
} from '@taiga-ui/kit';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { VEHICLES } from '@shared/constants/vehicles';
import { Store } from '@ngxs/store';
import {
  catchError,
  combineLatest,
  defaultIfEmpty,
  delay,
  EMPTY,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { AlertService } from '@shared/services/alert.service';
import { TuiActiveZoneModule } from '@taiga-ui/cdk';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { FormsService } from '@shared/services/forms.service';
import { Router } from '@angular/router';
import { AuthState } from '@store/auth/auth.state';
import { ControlService } from '@shared/services/control.service';
import { Cargo } from '../../../../types/cargo';
import { CargoLoadingService } from '../../services/cargo-loading.service';
import { CargoLoadingState } from '../../store/cargo-loading.state';
import { RouteNames } from '../../../../route-names';

@Component({
  selector: 'app-main-info',
  standalone: true,
  imports: [
    TuiGroupModule,
    TuiInputModule,
    TuiInputNumberModule,
    InputDateComponent,
    AutocompleteComponent,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    AsyncPipe,
    TuiActiveZoneModule,
    TuiButtonModule,
  ],
  templateUrl: './main-info.component.html',
  styleUrl: './main-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainInfoComponent implements OnInit {
  update$ = new Subject<void>();
  moveToCalculation$ = new Subject<void>();
  form = this.fb.group({
    id: this.fb.nonNullable.control({ value: 0, disabled: true }),
    creationDate: this.fb.nonNullable.control({
      value: '',
      disabled: true,
    }),
    transportType: '',
    trailerType: '',
    sellerJson: this.forms.getSellerJsonForm(),
    uploadDate: '',
    unloadDate: '',
  });

  readonly vehicles = VEHICLES;
  sellers$ = this.cargoCatalogsService.sellers$;

  cargo$ = this.store.select(CargoLoadingState.getSelectedCargo);

  canMoveToCalculation$ = combineLatest([
    this.cargo$,
    this.cargoLoadingService.readonly$,
  ]).pipe(
    map(([cargo, readonly]) => {
      if (readonly) {
        return false;
      }
      return !!cargo?.grossWeight;
    }),
  );

  isGreenlandUser$ = this.store.select(AuthState.isGreenlandUser);
  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
    protected readonly cargoCatalogsService: CargoCatalogsService,
    private readonly alert: AlertService,
    private readonly cargoLoadingService: CargoLoadingService,
    private readonly forms: FormsService,
    private readonly router: Router,
    private readonly dialogs: TuiDialogService,
    private readonly controlService: ControlService,
  ) {}

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
  }

  onMoveToCalculation() {
    this.moveToCalculation$
      .pipe(
        switchMap(() =>
          this.dialogs
            .open<boolean>(TUI_PROMPT, {
              data: {
                content:
                  '<h1 class="font-bold text-lg">Перевести в статус "В пути" ?<h1>',
                yes: 'Подтвердить',
                no: 'Отмена',
              },
            })
            .pipe(filter(submit => submit)),
        ),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) =>
          this.validateCargo(cargo?.id ?? 0).pipe(catchError(() => EMPTY)),
        ),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => {
          if (!cargo) {
            return throwError(() => new Error('empty cargo'));
          }
          return this.cargoLoadingService
            .moveFromLoadingToOnTheWay(cargo.id)
            .pipe(
              map(() => cargo.id),
              catchError(err => {
                this.alert.showError(err.error);
                return of(null);
              }),
            );
        }),
        withLatestFrom(this.isGreenlandUser$),
        tap(([id, isGreenlandUser]) => {
          if (isGreenlandUser) {
            const path = [RouteNames.CARGO_ON_THE_WAY_PAGE, id].join('/');
            this.router.navigate([path]);
          } else {
            this.router.navigate([RouteNames.KANBAN_PAGE]);
          }
        }),
        catchError(() => {
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  validateCargo(value: number, skipText = 'Отправить в 1C') {
    return of(value).pipe(
      switchMap(id =>
        this.controlService.validate(id).pipe(
          map(records => records.filter(err => err.message.type !== 'info')),
          switchMap(errorsAndWarnings => {
            if (errorsAndWarnings.length) {
              const errors = errorsAndWarnings.filter(
                err => err.message.type === 'error',
              ).length;
              const warnings = errorsAndWarnings.length - errors;
              return this.dialogs
                .open<boolean>(TUI_PROMPT, {
                  data: {
                    content: `
                      <h1 class="font-bold text-lg">Выявлены ошибки при проведении контроля</h1>
                      <div class="flex gap-x-1">
                        ${errors > 0 ? `<div class="bg-red-600 text-white rounded-xl w-fit mr-2 min-w-[24px] text-center px-2">Ошибки (${errors})</div>` : ''}
                        ${warnings > 0 ? `<div class="min-w-[24px] text-center bg-yellow-400 rounded-xl w-fit px-2">Предупреждения (${warnings})</div>` : ''}
                      </div>
                    `,
                    yes: 'Исправить ошибки',
                    no: skipText,
                  },
                })
                .pipe(
                  defaultIfEmpty(true),
                  switchMap(fixErrors => {
                    if (fixErrors) {
                      return throwError(() => new Error('Ошибки в контроле'));
                    }
                    return of(null);
                  }),
                );
            }
            return of(null);
          }),
        ),
      ),
    );
  }

  fillSellerJson(sellerId: Nullable<string>) {
    const sellerJsonControl = this.form.controls.sellerJson;
    const seller =
      this.cargoCatalogsService.sellerMap.get(sellerId?.toString() ?? '') ??
      null;
    if (seller) {
      sellerJsonControl.patchValue(seller);
    } else {
      sellerJsonControl.reset();
      sellerJsonControl.markAsDirty();
    }
  }

  fillFormOnStateChange() {
    this.store
      .select(CargoLoadingState.getSelectedCargo)
      .pipe(
        filter(state => state !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(cargo => {
        if (cargo) {
          this.form.patchValue(cargo);
        }
      });
  }

  updateOnChanges() {
    this.update$
      .pipe(
        delay(0),
        filter(() => !this.form.pristine),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => {
          const payload: Cargo = {
            ...cargo,
            ...this.form.getRawValue(),
          } as Cargo;
          return this.cargoLoadingService.updateCargo(payload);
        }),
        tap(() => {
          this.form.markAsPristine();
          this.form.markAsUntouched();
          this.alert.showSuccess();
        }),
        catchError(err => {
          this.alert.showError(err.error?.error || 'Ошибка');
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  disableWhenReadonly() {
    this.cargoLoadingService.readonly$
      .pipe(
        tap(readonly => {
          if (readonly) {
            this.form.disable();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.updateOnChanges();
    this.fillFormOnStateChange();
    this.onMoveToCalculation();
    this.disableWhenReadonly();
  }
}
